import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { BarChart3, TrendingUp, Users, DollarSign, Menu, X, Bell, Settings, LogOut, Upload, ClipboardList, UserCog, ShieldOff, CheckSquare, Gift } from 'lucide-react';
import { logoutUser } from './api/auth';
import { sessionManager } from './api/sessionManager';
import { canAccessScreen } from './api/permissions';
import MoneyAnimation from './components/MoneyAnimation';
import DashboardPage from './pages/DashboardPage';
import UploadPage from './pages/UploadPage';
import CustomersPage from './pages/CustomersPage';
import UsersPage from './pages/UsersPage';
import RevenuePage from './pages/RevenuePage';
import AnalyticsPage from './pages/AnalyticsPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import AddFormPage from './pages/AddFormPage';
import EmployeesPage from './pages/EmployeesPage';
import TasksPage from './pages/TasksPage';
import BirthdaysPage from './pages/BirthdaysPage';
import SMTPSettingsModal from './components/SMTPSettingsModal';

/* ── All screens with their permission key ─────────────────────────── */
const ALL_MENU_ITEMS = [
  { name: 'Dashboard', screen: 'Dashboard', icon: BarChart3,     path: '/dashboard' },
  { name: 'Customers', screen: 'Customers', icon: Users,         path: '/dashboard/customers' },
  { name: 'Users',     screen: 'Users',     icon: Users,         path: '/dashboard/users' },
  { name: 'Upload',    screen: 'Upload',    icon: Upload,        path: '/dashboard/upload' },
  { name: 'Add Form',  screen: 'Add Form',  icon: ClipboardList, path: '/dashboard/add-form' },
  { name: 'Employees', screen: 'Employees', icon: UserCog,       path: '/dashboard/employees' },
  { name: 'Tasks',     screen: 'Tasks',     icon: CheckSquare,   path: '/dashboard/tasks' },
  { name: 'Birthdays', screen: 'Birthdays', icon: Gift,          path: '/dashboard/birthdays' },
];

/* ── Redirect if not allowed, otherwise render children ────────────── */
const GuardedRoute = ({ allowed, fallback, children }) =>
  allowed ? children : <Navigate to={fallback} replace />;

/* ── Shown when user has no screen access at all ───────────────────── */
const NoAccessPage = () => (
  <div className="flex flex-col items-center justify-center h-full py-32 text-center">
    <ShieldOff size={48} className="text-gray-300 mb-4" />
    <h2 className="text-lg font-bold text-gray-700 mb-1">No Screen Access</h2>
    <p className="text-sm text-gray-400">You don't have access to any screens. Contact your administrator.</p>
  </div>
);

const AdminDashboard = ({ onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showAnimation, setShowAnimation] = useState(false);
  const [showSMTPSettings, setShowSMTPSettings] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const alreadyPlayed = sessionStorage.getItem('banking_crm_animation_played');
    if (!alreadyPlayed) {
      setShowAnimation(true);
      sessionStorage.setItem('banking_crm_animation_played', '1');
      const timer = setTimeout(() => setShowAnimation(false), 3500);
      return () => clearTimeout(timer);
    }
  }, []);

  /* ── Permissions ──────────────────────────────────────────────── */
  const user = sessionManager.getUser();
  const isAdmin = user?.role === 'admin';

  const canAccess = (screen) => isAdmin || canAccessScreen(screen);

  const menuItems = ALL_MENU_ITEMS.filter(item => canAccess(item.screen));

  // Where to redirect when a blocked route is hit
  const fallbackPath = menuItems[0]?.path ?? '/dashboard';

  /* ── Page title ───────────────────────────────────────────────── */
  const getPageTitle = () => {
    const pathMap = {
      '/dashboard':            'Dashboard',
      '/dashboard/customers':  'Customers',
      '/dashboard/users':      'Users',
      '/dashboard/revenue':    'Revenue',
      '/dashboard/analytics':  'Analytics',
      '/dashboard/upload':     'File Upload',
      '/dashboard/add-form':   'Add Form',
      '/dashboard/employees':  'Employees',
      '/dashboard/tasks':      'Tasks',
      '/dashboard/birthdays':  'Upcoming Birthdays'
    };
    if (location.pathname.startsWith('/dashboard/customers/')) return 'Customer Details';
    if (location.pathname.startsWith('/dashboard/users/')) return 'User Details';
    if (location.pathname.startsWith('/dashboard/add-form/')) return 'Add Form Details';
    if (location.pathname === '/dashboard/birthdays') return 'Upcoming Birthdays';
    return pathMap[location.pathname] || 'Dashboard';
  };

  const handleLogout = () => {
    logoutUser();
    sessionStorage.removeItem('banking_crm_animation_played');
    if (onLogout) onLogout();
    navigate('/', { replace: true });
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {showSMTPSettings && <SMTPSettingsModal onClose={() => setShowSMTPSettings(false)} />}
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/50 z-30 md:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        ${sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64 md:translate-x-0 md:w-20'}
        bg-white text-gray-900 transition-all duration-300 ease-in-out fixed h-full z-40 left-0 top-0 border-r border-gray-200 shadow-sm overflow-hidden
      `}>
        <div className="flex items-center justify-between p-6 border-b border-gray-100 min-w-[16rem]">
          <div className={`flex items-center gap-2 ${!sidebarOpen && 'md:justify-center'}`}>
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center font-bold text-white shrink-0">
              BC
            </div>
            <span className={`text-lg font-bold text-gray-900 transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 md:hidden'}`}>Banking</span>
          </div>
        </div>

        <nav className="mt-8 px-3 space-y-2 min-w-[16rem]">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            const isActive =
              location.pathname === item.path ||
              (item.path === '/dashboard/customers' && location.pathname.startsWith('/dashboard/customers/')) ||
              (item.path === '/dashboard/add-form' && location.pathname.startsWith('/dashboard/add-form/'));
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-primary-600'
                }`}
                title={!sidebarOpen ? item.name : undefined}
                onClick={() => {
                  // Auto-close sidebar on mobile after clicking a link
                  if (window.innerWidth < 768) setSidebarOpen(false);
                }}
              >
                <IconComponent size={20} className="shrink-0" />
                <span className={`font-medium transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 md:hidden'}`}>{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className={`${sidebarOpen ? 'md:ml-64' : 'md:ml-20'} flex-1 flex flex-col transition-all duration-300 ease-in-out min-w-0 min-h-screen`}>
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 hover:text-gray-900"
              >
                {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <h1 className="text-2xl font-bold text-gray-900">{getPageTitle()}</h1>
            </div>

            <div className="flex items-center gap-4">
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative">
                <Bell size={20} className="text-gray-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              <button onClick={() => setShowSMTPSettings(true)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <Settings size={20} className="text-gray-600" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 hover:text-primary-600"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto p-6 bg-gray-50 min-w-0 relative">
          {showAnimation && <MoneyAnimation />}

          {menuItems.length === 0 ? (
            <NoAccessPage />
          ) : (
            <Routes>
              <Route path="/" element={
                <GuardedRoute allowed={canAccess('Dashboard')} fallback={fallbackPath}>
                  <DashboardPage />
                </GuardedRoute>
              } />
              <Route path="/customers" element={
                <GuardedRoute allowed={canAccess('Customers')} fallback={fallbackPath}>
                  <CustomersPage />
                </GuardedRoute>
              } />
              <Route path="/users" element={
                <GuardedRoute allowed={canAccess('Customers')} fallback={fallbackPath}>
                  <UsersPage />
                </GuardedRoute>
              } />
              <Route path="/customers/:id" element={
                <GuardedRoute allowed={canAccess('Customers')} fallback={fallbackPath}>
                  <CustomerDetailPage />
                </GuardedRoute>
              } />
              <Route path="/users/:id" element={<GuardedRoute allowed={canAccess('Users')} fallback="/dashboard"><CustomerDetailPage /></GuardedRoute>} />
              <Route path="/upload" element={<GuardedRoute allowed={canAccess('Upload')} fallback="/dashboard"><UploadPage /></GuardedRoute>} />
              <Route path="/add-form" element={<GuardedRoute allowed={canAccess('Add Form')} fallback="/dashboard"><AddFormPage /></GuardedRoute>} />
              <Route path="/add-form/:id" element={<GuardedRoute allowed={canAccess('Add Form')} fallback="/dashboard"><CustomerDetailPage /></GuardedRoute>} />
              <Route path="/employees" element={<GuardedRoute allowed={canAccess('Employees')} fallback="/dashboard"><EmployeesPage /></GuardedRoute>} />
              <Route path="/tasks" element={<GuardedRoute allowed={canAccess('Tasks')} fallback="/dashboard"><TasksPage /></GuardedRoute>} />
              <Route path="/tasks/:employeeId" element={<GuardedRoute allowed={canAccess('Tasks')} fallback={fallbackPath}><TasksPage /></GuardedRoute>} />
              <Route path="/birthdays" element={<GuardedRoute allowed={canAccess('Birthdays')} fallback="/dashboard"><BirthdaysPage /></GuardedRoute>} />
              <Route path="/revenue" element={<Navigate to="/dashboard" replace />} />
              <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to={fallbackPath} replace />} />
            </Routes>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
